'use client';

import classNames from 'classnames';
import {
    CLPPurchaseOption,
    useClpPurchaseContext,
} from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import {BlockList} from '@udemy-v2/react-core-components';
import {useClpNoticeContext} from '@/features/course-landing-page/context/clp-notice-context-provider.react-component';
import {publishEvent} from '@udemy-v2/event-tracking';
import {Icon} from '@udemy-v2/icons';
import styles from './subscription-purchase-content.module.scss';
import {useFormatNumber, useI18n} from '@udemy-v2/i18n';
import {useFormatCurrency} from '@udemy-v2/react-currency-formatting';
import {CollapsiblePurchaseSection} from '@/features/course-landing-page/components/collapsible-purchase-section/collapsible-purchase-section.react-component';
import {
    aggregateCollectionStats,
    FALLBACK_COLLECTION_STATS,
    PlanPeriod,
    PlanPeriodStyle,
    SubscribeFromPageCookie,
    SubscriptionCTA,
    SubscriptionLearnMoreEvent,
} from '@udemy-v2/react-subscription-browse';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';

const UI_REGION = 'purchase_section_tab0';
const SUBSCRIPTION_ATTRIBUTION_PATHS = ['/personal-plan', '/pricing'];

export const CollapsibleSubscriptionPurchaseContent = () => {
    const {gettext, interpolate} = useI18n();
    const {formatNumber} = useFormatNumber();
    const {
        unwrappedConsumerSubscriptionPlan,
        subscriptionCTAConfiguration,
        setSelectedPurchaseOption,
    } = useClpPurchaseContext();

    const currencyFormatter = useFormatCurrency();
    const course = useClpCourse();
    const clpNoticeContext = useClpNoticeContext();
    const subscriptionPriceBoxNotice = clpNoticeContext?.subscriptionPriceBoxNoticeData;
    const contentCollections = unwrappedConsumerSubscriptionPlan?.contentCollections;
    const subscriptionPlanStats = contentCollections
        ? aggregateCollectionStats(
              contentCollections.map((contentCollection) => contentCollection.stats),
          )
        : FALLBACK_COLLECTION_STATS;

    const {selectedPurchaseOption} = useClpPurchaseContext();
    const isPersonalPlanPrimaryPurchaseOptions =
        selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION;

    const discountMonthlyPrice =
        unwrappedConsumerSubscriptionPlan?.selectedDiscount?.subscriptionDiscountMonthlyPrice;
    const formattedDiscountMonthlyPrice = discountMonthlyPrice
        ? currencyFormatter.formatCurrency(discountMonthlyPrice)
        : undefined;

    const learnMoreText = subscriptionPriceBoxNotice?.subtitle_link_text ?? gettext('Learn more');
    const subscriptionContextLearnMoreUrl =
        unwrappedConsumerSubscriptionPlan?.urlLearnMore ?? 'https://udemy.com/personal-plan/';
    const learnMoreUrl =
        subscriptionPriceBoxNotice?.subtitle_link_url ?? subscriptionContextLearnMoreUrl;
    const learnMoreTarget =
        subscriptionPriceBoxNotice?.subtitle_link_target === 'new_tab' ? '_blank' : '_self';

    const handleLearnMoreClick = () => {
        // When navigating to Personal Plan info / plan selection pages from CLP, set the
        // subscribe_from_page cookie so subscription checkout can attribute to this course.
        if (SUBSCRIPTION_ATTRIBUTION_PATHS.some((path) => learnMoreUrl.includes(path))) {
            SubscribeFromPageCookie.set('course_landing_page', parseInt(course.id, 10));
        }

        if (unwrappedConsumerSubscriptionPlan) {
            publishEvent(
                new SubscriptionLearnMoreEvent({
                    planId: unwrappedConsumerSubscriptionPlan.id,
                    planType: unwrappedConsumerSubscriptionPlan.productType,
                    uiRegion: UI_REGION,
                    planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds,
                }),
            );
        }
    };

    const handleCtaClick = () => {
        SubscribeFromPageCookie.set('course_landing_page', parseInt(course.id, 10));
    };

    if (!unwrappedConsumerSubscriptionPlan) {
        return null;
    }

    const title = (
        <>
            <span className={classNames('ud-text-md')}>{gettext('Subscribe and save')}</span>
            <PlanPeriod
                listPriceText={unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount}
                trialPeriodDays={unwrappedConsumerSubscriptionPlan.trialPeriodDays}
                style={PlanPeriodStyle.TITLE}
                discountPriceText={formattedDiscountMonthlyPrice}
            />
        </>
    );

    return (
        <CollapsiblePurchaseSection
            defaultExpanded={isPersonalPlanPrimaryPurchaseOptions}
            title={title}
            onExpand={() => setSelectedPurchaseOption(CLPPurchaseOption.SUBSCRIPTION)}
        >
            <BlockList padding="tight" size="small">
                <BlockList.Item icon={<Icon label="" size="xsmall" glyph="video-playlist" />}>
                    <span className="ud-text-sm">
                        {interpolate(
                            gettext('Access to %(courses_count)s+ top-rated courses'),
                            {courses_count: formatNumber(subscriptionPlanStats.numberOfCourses)},
                            true,
                        )}
                    </span>
                </BlockList.Item>
                <BlockList.Item icon={<Icon label="" size="xsmall" glyph="coupon" />}>
                    <span className="ud-text-sm">{gettext('Cancel anytime')}</span>
                </BlockList.Item>
                <BlockList.Item icon={<Icon label="" size="xsmall" glyph="info-outline" />}>
                    <a
                        className={styles['learn-more-link']}
                        data-purpose="subscription-learn-more-link"
                        href={learnMoreUrl}
                        target={learnMoreTarget}
                        onClick={handleLearnMoreClick}
                    >
                        {learnMoreText}
                    </a>
                </BlockList.Item>
            </BlockList>
            <SubscriptionCTA
                className={classNames(
                    styles['subscription-cta'],
                    styles['subscription-cta-collapsible'],
                )}
                uiRegion={UI_REGION}
                showSubtitle={false}
                data-purpose="subscription-purchase-button"
                offerCtaStyle={'primary'}
                onCTAClick={handleCtaClick}
                courseId={parseInt(course.id, 10)}
                includeTrialDaysOnCta={false}
                plan={unwrappedConsumerSubscriptionPlan}
                directCheckout={subscriptionCTAConfiguration?.directCheckout}
                selectPlanText={subscriptionCTAConfiguration?.selectPlanText}
                purchaseCtaText={subscriptionCTAConfiguration?.purchaseCtaText}
            />
        </CollapsiblePurchaseSection>
    );
};
